import React, { ReactNode, useEffect } from 'react';

declare global {
  namespace JSX {
    // tslint:disable-next-line: interface-name
    interface IntrinsicElements {
      'sg-error-page': {
        code: 404 | 500 | 503;
        theme?: 'dark' | 'standard';
        'action-button-label'?: string;
        'action-button-link'?: string;
        'contact-us'?: 'help-center' | string;
        fullscreen?: boolean;
        'description-label'?: string;
        'title-label'?: string;
      };
    }
  }
}

const AUTO_REFRESH_KEY = 'euc.automatic-refresh';
const automaticRefresh = () => {
  const hasAlreadyTriedToRefresh = sessionStorage.getItem(AUTO_REFRESH_KEY);
  if (hasAlreadyTriedToRefresh) {
    // the key in sessionStorage exists, meaning we already refresh the page...
    sessionStorage.removeItem(AUTO_REFRESH_KEY);
  } else {
    sessionStorage.setItem(AUTO_REFRESH_KEY, 'true');
    (window.location.reload as any)(true);
  }
};

const AutomaticRefreshPageOnCountriesError = () => {
  useEffect(() => {
    automaticRefresh();
  }, []);

  return null;
};

interface IErrorBoundaryState {
  error: Error | null;
}

interface IErrorBoundaryProps {
  children: ReactNode;
}

export class ErrorBoundary extends React.Component<IErrorBoundaryProps, IErrorBoundaryState> {
  public state: IErrorBoundaryState = {
    error: null,
  };

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI.
    return { error };
  }

  public componentDidCatch(errorDetected: Error, errorInfo: React.ErrorInfo) {
    console.error(
      `ErrorBoundary error[${errorDetected.message}] stack[${errorDetected.stack}] errorInfo[${errorInfo}]`
    );
  }

  public render() {
    const { error } = this.state;
    const { children } = this.props;

    if (error) {
      return (
        <div style={{ height: '100vh' }}>
          <sgwt-help-center
            sg-connect-support="sg-connect-v2"
            application-id="end-user-computing"
            default-send-to={window.globalConfiguration.contactEmail}
            message-only="true"
            message-template={`Hello support.
            
I encountered an issue while using the service.
-----------------
Technical details
Error message: ${error.message}
Stack trace: ${error.stack}
            `}
          />

          <sg-error-page
            code={500}
            action-button-label="Back to home"
            action-button-link="/"
            contact-us="help-center"
            fullscreen={true}
            description-label={error.message}
            title-label="End User Computing - Error"
          />

          {error.message.includes('includes') && <AutomaticRefreshPageOnCountriesError />}
        </div>
      );
    }

    return children;
  }
}
